import { AxiosError } from 'axios';
import { defineStore } from 'pinia';
import templatesService from '@/api/templatesService';
import { CreateTemplateRequest } from '@/types/api/request/createTemplateTypes';
import { Template } from '@/types/template/template';
import { notifyApiError, notifyUnknownError } from '@/utils/notifiers';
import { useRefetchStore } from './useRefetchStore';

export const useTemplateStore = defineStore({
	id: 'template_store',
	state: () => ({
		templates: Array<Template>(),
		loading: false,
		deleting: false,
	}),
	actions: {
		async fetchTemplates(): Promise<void> {
			this.loading = true;

			try {
				const response = await templatesService.getTemplates();
				this.templates = response.data.data as Array<Template>;
			} catch (e: unknown) {
				notifyApiError(e as AxiosError);
				throw e;
			} finally {
				this.loading = false;
			}
		},

		async createTemplate(template: CreateTemplateRequest): Promise<void> {
			this.loading = true;

			const refetchStore = useRefetchStore();

			try {
				await this.fetchTemplates();
				this.loading = true;

				const existingTemplate = this.templates.find((t) => t.name === template.name);

				if (existingTemplate) {
					const isUpdatingSameTemplate = template.oldName && template.oldName === template.name;

					if (!isUpdatingSameTemplate) {
						throw new Error(`A template with the name "${template.name}" already exists.`);
					}
				}

				await templatesService.createTemplate(template);
				await refetchStore.refetchAll();
			} catch (e: unknown) {
				if (e instanceof AxiosError) {
					notifyApiError(e);
				} else {
					notifyUnknownError(e);
				}

				throw e;
			} finally {
				this.loading = false;
			}
		},

		async updateTemplate(template: CreateTemplateRequest): Promise<void> {
			this.loading = true;

			const refetchStore = useRefetchStore();

			try {
				await templatesService.updateTemplate(template);
				await refetchStore.refetchAll();
			} catch (e: unknown) {
				notifyApiError(e as AxiosError);
				throw e;
			} finally {
				this.loading = false;
			}
		},

		async deleteTemplate(name: string): Promise<void> {
			this.deleting = true;

			const refetchStore = useRefetchStore();

			try {
				await templatesService.deleteTemplate(name);
				this.deleting = false;
				await refetchStore.refetchAll();
			} catch (e: unknown) {
				notifyApiError(e as AxiosError);
				throw e;
			} finally {
				this.deleting = false;
			}
		},
	},
});
